"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ArrowRight, Loader } from "lucide-react";
function ReservationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-blue-900 bg-white">
        <p className="font-default text-2xl text-gray-900 mb-3" style={{
        letterSpacing: "-0.02em"
      }}>
          Success! <span className="italic text-blue-900">Your request is received.</span>
        </p>
        <p className="text-sm text-gray-600 mb-6 leading-relaxed">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white rounded-lg uppercase tracking-[0.08em] text-xs font-bold">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="full_name" rules={{
      required: "Full name is required"
    }} render={({
      field
    }) => <FormItem data-index={0}>
            <FormLabel className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-gray-500">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="John Smith" {...field} className="h-12 bg-gray-100 border-0 border-b border-gray-300 px-3 focus-visible:ring-0 focus-visible:border-blue-900 focus-visible:border-b-2 transition-colors duration-200 text-gray-900" />
            </FormControl>
            <FormDescription className="text-xs text-gray-500">
              Enter your full legal name.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-gray-500">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" {...field} className="h-12 bg-gray-100 border-0 border-b border-gray-300 px-3 focus-visible:ring-0 focus-visible:border-blue-900 focus-visible:border-b-2 transition-colors duration-200 text-gray-900" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone number is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-gray-500">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="(555) 123-4567" {...field} className="h-12 bg-gray-100 border-0 border-b border-gray-300 px-3 focus-visible:ring-0 focus-visible:border-blue-900 focus-visible:border-b-2 transition-colors duration-200 text-gray-900" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="purchase_type" rules={{
        required: "Purchase type is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-gray-500">
                Purchase Type
              </FormLabel>
              <FormControl>
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="h-12 w-full bg-gray-100 border-0 border-b border-gray-300 px-3 font-default text-sm text-gray-900 focus-visible:ring-0 focus-visible:border-blue-900 focus-visible:border-b-2 transition-colors duration-200">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Automotive">Automotive</SelectItem>
                    <SelectItem value="Real Estate">Real Estate</SelectItem>
                    <SelectItem value="Medical">Medical</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="budget" rules={{
        required: "Estimated budget is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-gray-500">
                Estimated Budget
              </FormLabel>
              <FormControl>
                <Input type="number" placeholder="50000" {...field} className="h-12 bg-gray-100 border-0 border-b border-gray-300 px-3 focus-visible:ring-0 focus-visible:border-blue-900 focus-visible:border-b-2 transition-colors duration-200 text-gray-900" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="details" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-gray-500">
              Negotiation Details
            </FormLabel>
            <FormControl>
              <textarea placeholder="Tell us about the purchase you need help with..." rows={3} {...field} className="flex w-full bg-gray-100 border-0 border-b border-gray-300 px-3 py-2 text-sm outline-none focus-visible:border-blue-900 focus-visible:border-b-2 transition-colors duration-200 text-gray-900 placeholder:text-gray-400 resize-none" />
            </FormControl>
            <FormDescription className="text-xs text-gray-500">
              Optional: provide any specific concerns.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-red-600 bg-red-50">
          <p className="text-sm text-red-600">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 rounded-lg uppercase tracking-[0.08em] text-xs font-bold group bg-orange-600 text-white hover:bg-orange-700 shadow-md">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Processing...
          </> : <>
            Start Negotiation
            <ArrowRight className="ml-1 size-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
          </>}
      </Button>
    </Form>;
}
export default function StartYourNegotiation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1045" id="start-your-negotiation" className="bg-white py-28 md:py-40 overflow-hidden relative">
      <div ref={ref} className="max-w-[1320px] mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-6 relative" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 700ms cubic-bezier(0.22, 0.61, 0.36, 1), transform 700ms cubic-bezier(0.22, 0.61, 0.36, 1)"
        }}>
            <div className="flex items-center gap-3 mb-4">
              <span className="font-default text-[0.75rem] font-bold uppercase tracking-[0.18em] text-orange-600">
                § 01 — Start Your Negotiation
              </span>
            </div>

            <div className="flex items-center gap-2 mb-10">
              <div className="h-px flex-1 max-w-[120px] bg-gray-300" />
              <div className="size-3 bg-orange-600 rounded-sm" />
            </div>

            <div className="relative">
              <h2 className="font-default text-blue-900 relative" style={{
              fontSize: "clamp(2.25rem, 4.6vw, 4rem)",
              fontWeight: 800,
              letterSpacing: "-0.03em",
              lineHeight: 1.1
            }}>
                Take Control Of Your{" "}
                <span className="text-orange-600" style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateX(0)" : "translateX(-8px)",
                transition: "opacity 700ms 200ms cubic-bezier(0.22, 0.61, 0.36, 1), transform 700ms 200ms cubic-bezier(0.22, 0.61, 0.36, 1)",
                display: "inline-block"
              }}>
                  Next Purchase
                </span>
              </h2>
            </div>

            <p className="mt-8 text-gray-600 max-w-[420px]" style={{
            fontSize: "clamp(1.125rem, 1.5vw, 1.25rem)",
            lineHeight: 1.6
          }}>
              Our expert advocates are ready to help you secure the best possible terms for your high-stakes purchases.
            </p>

            <div className="mt-12 space-y-3 max-w-[420px]">
              <div className="flex items-center gap-3">
                <div className="h-px w-6 bg-gray-300" />
                <span className="font-default text-xs uppercase tracking-[0.12em] text-gray-500">
                  Mon–Fri · 9:00 AM – 6:00 PM
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="h-px w-6 bg-gray-300" />
                <span className="font-default text-xs uppercase tracking-[0.12em] text-gray-500">
                  (800) 555-0199
                </span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 700ms 120ms cubic-bezier(0.22, 0.61, 0.36, 1), transform 700ms 120ms cubic-bezier(0.22, 0.61, 0.36, 1)"
        }}>
            <div className="relative p-3 border border-gray-200 shadow-md rounded-lg">
              <div className="relative bg-white">
                <div className="p-5 md:p-10">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-gray-500">
                      Consultation Request
                    </span>
                  </div>

                  <h3 className="font-default text-blue-900 mb-1" style={{
                  fontSize: "clamp(1.25rem, 2vw, 1.75rem)",
                  fontWeight: 600,
                  letterSpacing: "0.01em",
                  lineHeight: 1.4
                }}>
                    Get Started Today
                  </h3>

                  <div className="flex items-center gap-2 mb-8">
                    <div className="h-px flex-1 bg-gray-200" />
                    <div className="size-2 bg-orange-600 rounded-lg" />
                    <div className="h-px flex-1 bg-gray-200" />
                  </div>

                  <FormProvider formKey="negotiation_form_services" formId="negotiation_form_services" sectionName="start-your-negotiation" defaultValues={{
                  full_name: "",
                  email: "",
                  phone: "",
                  purchase_type: "",
                  budget: "",
                  details: ""
                }} validationRules={{
                  full_name: {
                    required: true
                  },
                  email: {
                    required: true,
                    pattern: "email"
                  },
                  phone: {
                    required: true,
                    pattern: "phone"
                  },
                  purchase_type: {
                    required: true
                  },
                  budget: {
                    required: true
                  }
                }} submitText="Start Negotiation" successMessage="We have received your request. An advocate will contact you shortly." errorMessage="We couldn't process your request. Please try again.">
                    <ReservationForm />
                  </FormProvider>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}